/**
 * @fileoverview Hook UI Layer para gestión visual y opciones de estado de pago de órdenes de cliente en Inbox.
 * @module useCustomerOrderPaymentStatusUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

/**
 * Hook UI Layer para gestión visual y opciones de estado de pago de órdenes de cliente.
 *
 * @function useCustomerOrderPaymentStatusUI
 * @param {Object} order - Objeto de la orden
 * @param {Function} handleStatusChange - Handler para cambio de estado
 * @returns {Object} paymentStatusLabel, paymentStatusOptions, containerClassNamePaymentStatus, canceledOrderWithoutPayment
 * @see useCustomerOrderPaymentStatusActions
 */
export default function useCustomerOrderPaymentStatusUI(order, handleStatusChange) {
  const { t } = useTranslation('Orders');

  const canceledOrderWithoutPayment = useMemo(() =>
    order.status === 'canceled' && isEmpty(order.payment_transaction) && order.payment_status === 'payment_pending',
    [order.payment_transaction, order.payment_status, order.status]
  );

  const paymentStatesOptionsMap = useMemo(() => ([
    {
      value: 'payment_pending',
      label: t('payment_status.pending'),
      containerClassName: '!tw-bg-yellow-50 !tw-text-yellow-700',
      onClick: () => handleStatusChange('payment_pending'),
    },
    {
      value: 'payment_success',
      label: t('payment_status.success'),
      containerClassName: '!tw-bg-green-50 !tw-text-green-700',
      onClick: () => handleStatusChange('payment_success'),
    },
    {
      value: 'payment_failed',
      label: t('payment_status.failed'),
      containerClassName: '!tw-bg-red-50 !tw-text-red-700',
      onClick: () => handleStatusChange('payment_failed'),
    },
    {
      value: 'payment_refunded',
      label: t('payment_status.refunded'),
      containerClassName: '!tw-bg-violet-50 !tw-text-violet-700',
      onClick: () => handleStatusChange('payment_refunded'),
    },
  ]), [handleStatusChange, t]);

  const paymentStatusOptions = useMemo(() => {
    if (canceledOrderWithoutPayment) return [];
    switch (order.payment_status) {
      case 'payment_pending':
        return [paymentStatesOptionsMap[1]];
      case 'payment_success':
        return [paymentStatesOptionsMap[3]];
      case 'payment_failed':
        return [paymentStatesOptionsMap[0], paymentStatesOptionsMap[1]];
      default:
        return [];
    }
  }, [order?.payment_status, paymentStatesOptionsMap, canceledOrderWithoutPayment]);

  const paymentStatusLabel = useMemo(() => {
    if (canceledOrderWithoutPayment) {
      return t('payment_status.unprocessed');
    }
    const found = paymentStatesOptionsMap.find(option => option.value === order.payment_status);
    return found ? found.label : t('payment_status.unprocessed');
  }, [order?.payment_status, paymentStatesOptionsMap, canceledOrderWithoutPayment, t]);

  const containerClassNamePaymentStatus = useMemo(() => {
    if (canceledOrderWithoutPayment) {
      return '!tw-bg-gray-50 !tw-text-gray-700';
    }
    const found = paymentStatesOptionsMap.find(option => option.value === order.payment_status);
    return found ? found.containerClassName : '';
  }, [order.payment_status, canceledOrderWithoutPayment, paymentStatesOptionsMap]);

  return {
    paymentStatusLabel,
    paymentStatusOptions,
    containerClassNamePaymentStatus,
    canceledOrderWithoutPayment
  };
}
